import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Download, Gift } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyerPayoutsPage = () => {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState({ upcoming: [], history: [], message: '' });
  const [loading, setLoading] = useState(true);
  const [gifts, setGifts] = useState([]);

  useEffect(() => {
    fetchPayouts();
    fetchGifts();
  }, []);

  const fetchGifts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/buyer/gifts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGifts(res.data?.gifts || []);
    } catch (e) {
      // Non-blocking
    }
  };

  const fetchPayouts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/buyer/payouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || {};
      setPayouts({
        upcoming: Array.isArray(data.upcoming) ? data.upcoming : [],
        history: Array.isArray(data.history) ? data.history : [],
        message: data.message || '',
      });
    } catch (error) {
      console.error('Error fetching payouts:', error);
      setPayouts({ upcoming: [], history: [], message: '' });
    } finally {
      setLoading(false);
    }
  };

  const exportPayoutsCSV = () => {
    const historyList = Array.isArray(payouts?.history) ? payouts.history : [];
    if (historyList.length === 0) {
      toast.error('No payout history to export');
      return;
    }
    const headers = ['Date', 'Portfolio', 'Amount', 'Method', 'Status', 'TX Hash'];
    const rows = historyList.map(p => [
      p.paid_date,
      p.portfolio_name,
      p.method === 'USD' ? `$${p.amount}` : `${p.amount} ${p.crypto_token || 'ETH'}`,
      p.method,
      p.status,
      p.tx_hash || 'N/A'
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const getMethodBadge = (method) => {
    const styles = {
      USD: { bg: 'rgba(71, 255, 176, 0.1)', color: '#47FFB0' },
      Crypto: { bg: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF' },
      Split: { bg: 'rgba(255, 193, 7, 0.1)', color: '#FFC107' }
    };
    const style = styles[method] || styles.USD;
    return <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', fontWeight: 600, background: style.bg, color: style.color }}>{method}</span>;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#0B0B0B' }}>
        Loading payouts...
      </div>
    );
  }

  const safeUpcoming = Array.isArray(payouts?.upcoming) ? payouts.upcoming : [];
  const safeHistory = Array.isArray(payouts?.history) ? payouts.history : [];

  return (
    <div data-testid="buyer-payouts-page" style={{ padding: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0B0B0B', marginBottom: '8px' }}>
            Payouts
          </h1>
          <p style={{ fontSize: '16px', color: '#999' }}>
            Track your earnings and payment history
          </p>
        </div>
        <button
          onClick={exportPayoutsCSV}
          data-testid="export-csv-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#4B9DFF',
            color: '#FFF',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {/* Message banner if present */}
      {payouts.message && (
        <div data-testid="payouts-message" style={{
          padding: '16px 20px', marginBottom: '24px', borderRadius: '12px',
          background: 'rgba(75, 157, 255, 0.08)', border: '1px solid rgba(75, 157, 255, 0.2)',
          color: '#4B9DFF', fontSize: '14px',
        }}>
          {payouts.message}
        </div>
      )}

      {/* Kickback Gifts section */}
      <div data-testid="kickback-gifts-section" style={{
        background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12,
        padding: 20, marginBottom: 24,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Gift size={18} color="#6C5CE7" />
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Kickback Gifts</h2>
            <span style={{
              padding: '2px 8px', borderRadius: 999,
              background: '#EEF2FF', color: '#6C5CE7',
              fontSize: 11, fontWeight: 700,
            }}>{gifts.length}</span>
          </div>
          <button
            data-testid="view-all-gifts-btn"
            onClick={() => navigate('/buyer/gifts')}
            style={{
              padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
              background: 'transparent', color: '#6C5CE7',
              border: '1px solid #6C5CE7', fontSize: 12, fontWeight: 700,
            }}
          >View all</button>
        </div>
        {gifts.length === 0 ? (
          <div style={{ color: '#6B7280', fontSize: 13 }}>No kickback gifts yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {gifts.slice(0, 3).map((g) => (
              <div
                key={g.id}
                data-testid={`payouts-gift-row-${g.id}`}
                onClick={() => navigate('/buyer/gifts')}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: 10, background: '#F9FAFB', borderRadius: 8, cursor: 'pointer',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{g.portfolio_name}</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>{g.units} unit(s) · {g.status}</div>
                </div>
                <div style={{ fontWeight: 800, color: '#6C5CE7' }}>${Number(g.amount_usd).toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming Payouts */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #E6E8EF',
        marginBottom: '24px'
      }}>
        <h3 style={{ color: '#4B9DFF', fontSize: '18px', marginBottom: '16px' }}>Upcoming Payouts</h3>
        {safeUpcoming.length > 0 ? safeUpcoming.map((p) => (
          <div key={p.id} data-testid={`buyer-payout-row-${p.id}`}
            onClick={() => navigate(`/buyer/payouts/${p.id}`)}
            style={{ padding: '16px', background: 'rgba(75, 157, 255, 0.05)', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#0B0B0B', fontWeight: 500 }}>{p.portfolio_name}</div>
                <div style={{ color: '#999', fontSize: '13px' }}>
                  {p.cycle_name ? `Cycle: ${p.cycle_name}` : `Due: ${p.scheduled_date || '-'}`}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#47FFB0', fontWeight: 600 }}>
                  {(p.currency === 'USD' || p.method === 'USD' || p.method === 'ACH')
                    ? `$${Number(p.amount_usd ?? p.amount ?? 0).toFixed(2)} ${p.currency || 'USD'}`
                    : `${p.amount_crypto ?? p.amount ?? 0} ${p.token || p.currency || 'ETH'}`}
                </div>
                {getMethodBadge(p.method)}
              </div>
            </div>
          </div>
        )) : (
          <p style={{ color: '#666', textAlign: 'center', padding: '20px 0' }}>No upcoming payouts</p>
        )}
      </div>

      {/* History */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #E6E8EF'
      }}>
        <h3 style={{ color: '#4B9DFF', fontSize: '18px', marginBottom: '16px' }}>Payout History</h3>
        {safeHistory.length > 0 ? safeHistory.map((p) => (
          <div key={p.id} data-testid={`buyer-payout-row-${p.id}`}
            onClick={() => navigate(`/buyer/payouts/${p.id}`)}
            style={{ padding: '16px', background: 'rgba(71, 255, 176, 0.05)', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#0B0B0B', fontWeight: 500 }}>{p.portfolio_name}</div>
                <div style={{ color: '#999', fontSize: '13px' }}>{p.cycle_name || p.paid_date}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#47FFB0', fontWeight: 600 }}>
                  {(p.currency === 'USD' || p.method === 'USD' || p.method === 'ACH')
                    ? `$${Number(p.amount_usd ?? p.amount ?? 0).toFixed(2)} ${p.currency || 'USD'}`
                    : `${p.amount_crypto ?? p.amount ?? 0} ${p.crypto_token || p.currency || 'ETH'}`}
                </div>
                {getMethodBadge(p.method)}
              </div>
            </div>
          </div>
        )) : (
          <p style={{ color: '#666', textAlign: 'center', padding: '20px 0' }}>No payout history</p>
        )}
      </div>
    </div>
  );
};

export default BuyerPayoutsPage;
